import toast, { type Renderable } from 'react-hot-toast';

interface ErrorToastOptions {
  action?: {
    label: string;
    onClick: () => void;
  };
  /**
   * Duração em ms. Por padrão o toast de erro é persistente (`Infinity`) e só
   * fecha pelo botão OK — combinado com o cliente: erro não some sozinho.
   */
  duration?: number;
  /**
   * Id estável para deduplicar erros repetidos (ex.: perda de conexão). Quando
   * informado, o react-hot-toast atualiza o toast existente em vez de empilhar.
   */
  id?: string;
  /** Rótulo do botão de dispensar. Padrão: "OK". */
  dismissLabel?: string;
}

// Success toast with custom styling
export const showSuccessToast = (message: string) => {
  return toast.success(message, {
    duration: 5000,
    style: {
      background: '#10b981',
      color: '#fff',
      fontWeight: '500',
    },
    iconTheme: {
      primary: '#fff',
      secondary: '#10b981',
    },
  });
};

// Error toast with custom styling and optional action
export const showErrorToast = (message: string, options?: ErrorToastOptions) => {
  // O botão OK e a persistência são aplicados globalmente pelo <Toaster> a
  // todo toast de erro (ver App.tsx). Aqui só renderizamos a mensagem e o
  // botão de ação opcional (ex.: "Tentar Novamente").
  return toast.error(
    (t) => (
      <div className="flex items-center justify-between gap-3">
        <span className="flex-1">{message}</span>
        {options?.action && (
          <button
            type="button"
            onClick={() => {
              options.action!.onClick();
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 bg-white/20 rounded-md hover:bg-white/30 transition-colors text-sm font-medium whitespace-nowrap"
          >
            {options.action.label}
          </button>
        )}
      </div>
    ),
    {
      id: options?.id,
      // Erro persiste até o usuário fechar no OK, salvo duração explícita.
      duration: options?.duration ?? Infinity,
      style: {
        background: '#ef4444',
        color: '#fff',
        fontWeight: '500',
        maxWidth: '520px',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#ef4444',
      },
    }
  );
};

// Warning toast with custom styling
export const showWarningToast = (message: string) => {
  return toast(message, {
    duration: 4000,
    icon: '⚠️',
    style: {
      background: '#f59e0b',
      color: '#fff',
      fontWeight: '500',
    },
  });
};

interface InfoToastOptions {
  duration?: number;
  icon?: Renderable;
  ariaLive?: 'assertive' | 'off' | 'polite';
  role?: 'status' | 'alert';
  className?: string;
}

// Info toast with custom styling
export const showInfoToast = (message: string, options?: InfoToastOptions) => {
  return toast(message, {
    duration: options?.duration ?? 5000,
    icon: options?.icon ?? 'ℹ️',
    className: options?.className,
    style: {
      background: '#3b82f6',
      color: '#fff',
      fontWeight: '500',
    },
    ariaProps: {
      role: options?.role ?? 'status',
      'aria-live': options?.ariaLive ?? 'polite',
    },
  });
};

// Loading toast
export const showLoadingToast = (message: string) => {
  return toast.loading(message, {
    style: {
      background: '#6b7280',
      color: '#fff',
      fontWeight: '500',
    },
  });
};

// Custom toast with promise handling
export const showPromiseToast = <T,>(
  promise: Promise<T>,
  {
    loading,
    success,
    error,
  }: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: Error) => string);
  }
) => {
  return toast.promise(promise, {
    loading,
    success,
    error,
  });
};

// Dismiss all toasts
export const dismissAllToasts = () => {
  toast.dismiss();
};

// Dismiss specific toast
export const dismissToast = (toastId: string) => {
  toast.dismiss(toastId);
};

// Export toast as named export and default
export { toast };
export default toast;
